import React, { FC, MouseEventHandler } from 'react';
import { makeStyles, withStyles, Theme, createStyles } from '@material-ui/core/styles';
import { IconButton, Tab, Tabs, Tooltip, Chip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

interface TabParams {
  id: number;
  name: string;
  module?: string;
  count?: number;
}

interface Props {
  tabs: TabParams[];
  selectedTabId: number;
  showCloseButton?: boolean;
  handleCloseClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSelect: (tabId: number) => void;
}

const AntTabs = withStyles({
  indicator: {
    backgroundColor: 'inherit'
  }
})(Tabs);

const AntTab: any = withStyles((theme: Theme) =>
  createStyles({
    root: {
      textTransform: 'none',
      color: 'textSecondary',
      minWidth: 50,
      minHeight: 40,
      fontWeight: theme.typography.fontWeightRegular,
      marginRight: theme.spacing(1),
      '&:hover': {
        color: '#53A0BE',
        opacity: 1
      },
      '&$selected': {
        color: '#53A0BE',
        background: theme.palette.primary.light,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        borderRadius: 10,
        fontWeight: theme.typography.fontWeightMedium
      },
      '&:focus': {
        color: '#53A0BE'
      }
    },
    selected: {}
  })
)(props => <Tab disableRipple {...props} wrapped />);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  padding: {
    padding: theme.spacing(4)
  },
  closeButton: {
    marginLeft: 70
  },
  closeIcon: {
    fontSize: 14
  },
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  countChip: {
    height: 24,
    marginLeft: theme.spacing(0.5),
    backgroundColor: '#53A0BE',
    color: '#ffffff',
    fontSize: '0.75rem',
    fontWeight: 600
  }
}));

/**
 * Which tab is selected belongs to the page, not to this strip.
 *
 * It used to seed a copy of `selectedTabId` into state at mount and then ignore the prop, which held
 * as long as clicking was the only way the selection ever moved. It is not: a link can now open a
 * record straight onto one of its sections, and the strip sat on the old tab while the page below it
 * showed the new one.
 */
const CustomizedTabs: FC<Props> = props => {
  const { tabs, showCloseButton, onSelect, selectedTabId, handleCloseClick } = props;
  const classes = useStyles();

  const handleChange = (tabId: number): MouseEventHandler => event => {
    onSelect(tabId);
  };

  const renderIcon = () => {
    return (
      <Tooltip title={'Delete'}>
        <IconButton className={classes.closeButton} onClick={handleCloseClick}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Tooltip>
    );
  };

  const renderTabLabel = (tab: TabParams) => (
    <div className={classes.tabLabel}>
      <span>{tab.name}</span>
      {tab.count !== undefined && tab.count > 0 && <Chip label={tab.count} size='small' className={classes.countChip} />}
    </div>
  );

  return (
    <div className={classes.root}>
      <AntTabs value={selectedTabId} aria-label='ant' variant='scrollable'>
        {tabs.map(tab => (
          <AntTab key={tab.id} value={tab.id} label={renderTabLabel(tab)} onClick={handleChange(tab.id)} icon={showCloseButton ? renderIcon() : ''} />
        ))}
      </AntTabs>
    </div>
  );
};

export default CustomizedTabs;
